// Yango Dispatch Console — dispatch deliveries via Yango third-party service
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/admin/AdminLayout";
import { SearchInput } from "@/components/admin/shared/SearchInput";
import { YangoDispatchList } from "@/components/admin/delivery/YangoDispatchList";
import { YangoCostSummary } from "@/components/admin/delivery/YangoCostSummary";
import { useYangoDispatches } from "@/hooks/admin/useYango";
import { Button, Skeleton } from "@cieda/djayma-ui";
import { RefreshCw } from "lucide-react";
import type { YangoDispatchStatus } from "@/lib/admin-api/yango";

// ---------------------------------------------------------------------------
// Status filter options
// ---------------------------------------------------------------------------

const STATUS_FILTERS: readonly {
  readonly value: YangoDispatchStatus | "all";
  readonly labelKey: string;
}[] = [
  { value: "all", labelKey: "yango.filter.all" },
  { value: "pending", labelKey: "yango.filter.pending" },
  { value: "searching_driver", labelKey: "yango.filter.searching" },
  { value: "in_transit", labelKey: "yango.filter.in_transit" },
  { value: "delivered", labelKey: "yango.filter.delivered" },
  { value: "failed", labelKey: "yango.filter.failed" },
  { value: "cancelled", labelKey: "yango.filter.cancelled" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Yango() {
  const { t } = useTranslation("logistics");

  // Filters state
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    YangoDispatchStatus | "all"
  >("all");

  const params = {
    page,
    limit: 20,
    status: statusFilter === "all" ? null : statusFilter,
    q: search || null,
  };

  const { data, isLoading, isError, refetch } = useYangoDispatches(params);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback(
    (value: YangoDispatchStatus | "all") => {
      setStatusFilter(value);
      setPage(1);
    },
    [],
  );

  const handleDispatchSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold text-stone-900">
              {t("yango.title")}
            </h1>
            <p className="mt-1 text-sm text-stone-500">
              {t("yango.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {t("yango.refresh")}
            </Button>
          </div>
        </div>

        {/* KPI summary */}
        {isLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-lg border border-stone-200 bg-white p-5"
              >
                <Skeleton className="mb-2 h-4 w-20" />
                <Skeleton className="h-6 w-16" />
              </div>
            ))}
          </div>
        ) : data?.summary ? (
          <YangoCostSummary
            totalDispatches={data.summary.total_dispatches}
            totalCostFcfa={data.summary.total_cost_fcfa}
            pendingCount={data.summary.pending_count}
            inTransitCount={data.summary.in_transit_count}
            deliveredCount={data.summary.delivered_count}
            failedCount={data.summary.failed_count}
          />
        ) : null}

        {/* Filters bar */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="w-full sm:max-w-xs">
            <SearchInput
              value={search}
              onChange={handleSearchChange}
              placeholder={t("yango.search_placeholder")}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {STATUS_FILTERS.map((filter) => (
              <Button
                key={filter.value}
                size="sm"
                variant={
                  statusFilter === filter.value ? "default" : "outline"
                }
                onClick={() => handleStatusChange(filter.value)}
                className={
                  statusFilter === filter.value
                    ? "bg-amber-600 hover:bg-amber-700"
                    : ""
                }
              >
                {t(filter.labelKey)}
              </Button>
            ))}
          </div>
        </div>

        {/* Error state */}
        {isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
            <p className="text-sm text-red-700">{t("yango.error")}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="mt-2"
            >
              {t("yango.retry")}
            </Button>
          </div>
        )}

        {/* Dispatch list */}
        {!isError && (
          <YangoDispatchList
            dispatches={data?.dispatches ?? []}
            pagination={
              data?.pagination ?? { page: 1, limit: 20, total: 0 }
            }
            isLoading={isLoading}
            onPageChange={handlePageChange}
            onDispatchSuccess={handleDispatchSuccess}
          />
        )}
      </div>
    </AdminLayout>
  );
}
